import React, { useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faTimes, faFile, faTrash, faChartLine } from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../utils/apiConfig';
import './OptimizeCampaignModal.css';
import { OptimizeWindowFields } from './OptimizeWindowFields';
import { buildOptimizeDateParams, DEFAULT_OPTIMIZE_PRESET } from '../utils/optimizeDateRange';

const OptimizeCampaignModal = ({ isOpen, onClose, campaign, platformCampaignId, accountId, user, onOptimizeComplete, analysisKind = null }) => {
  // 'all' = the Run Full Analysis button (DV360): the same window/focus form,
  // but the run it starts is the three focused analyses, so every label must
  // say that — a modal titled "Optimize Campaign" here reads as the wrong run.
  const isFullAnalysis = analysisKind === 'all';
  const actionLabel = isFullAnalysis ? 'Run Full Analysis' : 'Optimize Campaign';
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  // Reporting window + optional focus for this run. Same control the sidekick
  // agent's run gate uses, so the two surfaces cannot disagree about what a
  // window means or which platforms can be told one.
  const [runOpts, setRunOpts] = useState({ preset: DEFAULT_OPTIMIZE_PRESET, customPrompt: '' });
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const isMadhive = campaign?.destination?.toLowerCase() === 'madhive';

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFiles(droppedFiles);
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    handleFiles(selectedFiles);
  };

  const handleFiles = (newFiles) => {
    // For MadHive, only accept ZIP files
    if (isMadhive) {
      const validFiles = newFiles.filter(file => file.name.toLowerCase().endsWith('.zip'));
      if (validFiles.length !== newFiles.length) {
        alert('Only ZIP files are accepted for MadHive campaign optimization');
      }
      setFiles(prev => [...prev, ...validFiles]);
    } else {
      setFiles(prev => [...prev, ...newFiles]);
    }
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleOptimize = async () => {
    // The window + focus ride every path out of this modal, so a MadHive run
    // (which also uploads a file) is steered the same way as any other.
    const runFields = {
      customPrompt: (runOpts.customPrompt || '').trim() || null,
      dateParams: buildOptimizeDateParams(runOpts, campaign?.destination),
    };
    if (!isMadhive) {
      // For non-MadHive campaigns, optimize without files
      onOptimizeComplete({ platformCampaignId, ...runFields });
      onClose();
      return;
    }

    if (files.length === 0) {
      alert('Please upload a ZIP file containing reporting metrics (CSV files)');
      return;
    }

    setIsUploading(true);

    try {
      // Step 1: Upload ZIP file to GCS
      const formData = new FormData();
      formData.append('file', files[0]); // Only one ZIP file
      formData.append('accountId', accountId);
      formData.append('userId', user?.userId || user?.id);

      const uploadResponse = await api.post(
        getApiUrl('/api/upload'),
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      const reportingMetricsPath = uploadResponse.data.data.gcsPath;

      console.log('📊 Reporting metrics uploaded:', reportingMetricsPath);

      // Step 2: Pass the reporting metrics path back to parent to handle the optimize call
      // This ensures the parent can properly refresh messages/activities after optimization
      onOptimizeComplete({
        platformCampaignId,
        reportingMetricsPath,
        ...runFields
      });
      onClose();
    } catch (error) {
      console.error('Optimize failed:', error);
      alert('Optimize failed: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsUploading(false);
    }
  };

  const getAcceptedFileTypes = () => {
    if (isMadhive) {
      return '.zip';
    }
    return '*';
  };

  const getFileTypeDescription = () => {
    if (isMadhive) {
      return 'ZIP files containing CSV reporting metrics';
    }
    return 'All files';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content optimize-campaign-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <FontAwesomeIcon icon={faChartLine} /> {actionLabel}
          </h2>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="modal-body">
          <div className="campaign-info">
            <p><strong>Campaign:</strong> {campaign?.name}</p>
            <p><strong>Platform:</strong> {campaign?.destination?.toUpperCase()}</p>
            {platformCampaignId && <p><strong>Platform Campaign ID:</strong> {platformCampaignId}</p>}
          </div>

          {/* Which slice of delivery data to analyse, and an optional steer.
              Above the file drop so the run is described before it is armed. */}
          <OptimizeWindowFields
            destination={campaign?.destination}
            value={runOpts}
            onChange={setRunOpts}
            className="opt-window-modal"
          />

          {isMadhive && (
            <>
              <div className="info-message">
                <p>Upload a ZIP file containing CSV files with reporting metrics for this campaign.</p>
              </div>

              <div 
                className={`file-drop-zone ${dragActive ? 'active' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <FontAwesomeIcon icon={faUpload} size="2x" />
                <p>Drag and drop ZIP file here or click to select</p>
                <small>{getFileTypeDescription()}</small>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept={getAcceptedFileTypes()}
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />

              {files.length > 0 && (
                <div className="selected-files">
                  <h4>Selected File:</h4>
                  {files.map((file, index) => (
                    <div key={index} className="file-item">
                      <FontAwesomeIcon icon={faFile} />
                      <span className="file-name">{file.name}</span>
                      <span className="file-size">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                      <button
                        className="remove-file-btn"
                        onClick={() => removeFile(index)}
                        disabled={isUploading}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {!isMadhive && (
            <div className="info-message">
              <p>
                {isFullAnalysis
                  ? 'Click "Run Full Analysis" to run the diagnostic, pacing and performance analyses — three separate results.'
                  : 'Click "Optimize" to analyze and optimize this campaign.'}
              </p>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={isUploading}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleOptimize}
            disabled={isUploading || (isMadhive && files.length === 0)}
            title={isMadhive && files.length === 0 ? 'Please upload a ZIP file with reporting metrics' : ''}
          >
            {isUploading ? (isFullAnalysis ? 'Starting analyses...' : 'Optimizing...') : actionLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OptimizeCampaignModal;

